import { Fragment, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { GlobalHeader } from '@/components/GlobalHeader';

const DAYS  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const HOURS  = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00'];

interface TimetableEntry {
  day: string;
  time: string;
  subject: string;
  type: 'class' | 'free';
}

interface TimetableData {
  entries: TimetableEntry[];
}

const timeLabelStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
  fontWeight: 600,
  textAlign: 'center',
  paddingTop: '6px',
};

const dayHeaderStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
  fontWeight: 600,
  textAlign: 'center',
};

export function ExamsPage() {
  const { t } = useTranslation();
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const { childId } = useAuthStore();
  const { data, isLoading } = useData<TimetableData>(
    childId ? `/children/${childId}/timetable` : null,
  );

  const entries = data?.entries ?? [];

  const getEntry = (time: string, day: string): TimetableEntry | undefined =>
    entries.find((e) => e.time === time && e.day === day);

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('exams_grid.title')}
          </h1>

          {/* Loading spinner */}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <div className="spinner" />
            </div>
          )}

          {/* Timetable grid */}
          {!isLoading && (
            <div
              style={{
                width: '100%',
                overflowX: 'auto',
                padding: '0 0 20px',
                scrollBehavior: 'smooth',
                boxSizing: 'border-box',
              }}
            >
              <div className="timetable-grid">

                {/* Day headers */}
                <div />
                {DAYS.map((day) => (
                  <div key={day} style={dayHeaderStyle}>{day}</div>
                ))}

                {/* Hour rows */}
                {HOURS.map((hour) => (
                  <Fragment key={hour}>
                    <div style={timeLabelStyle}>{hour}</div>
                    {DAYS.map((day) => {
                      const entry = getEntry(hour, day);

                      if (!entry) {
                        return <div key={day} />;
                      }

                      if (entry.type === 'class') {
                        return (
                          <div
                            key={day}
                            className="subject-card"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedSlot(entry.subject)}
                          >
                            {entry.subject}
                          </div>
                        );
                      }

                      return (
                        <div
                          key={day}
                          className="free-time-card"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedSlot(t('exams_grid.free_label'))}
                        >
                          {t('exams_grid.free_label')}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}

              </div>
            </div>
          )}
        </div>

        {/* Toast modal — appears above the tab bar when a slot is selected */}
        {selectedSlot !== null && (
          <div
            style={{
              position: 'absolute',
              bottom: 60,
              left: 0,
              right: 0,
              background: 'var(--text-dark)',
              color: 'white',
              padding: '16px',
              borderRadius: '12px 12px 0 0',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 20,
              boxSizing: 'border-box',
            }}
          >
            <span style={{ fontWeight: 500 }}>{t('exams_grid.editing_label', { slot: selectedSlot })}</span>
            <button
              type="button"
              onClick={() => setSelectedSlot(null)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {t('exams_grid.close')}
            </button>
          </div>
        )}

        <BottomTabBar activeTab="Exams" />

      </div>
    </div>
  );
}
